import React, { useCallback } from 'react';

import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/hooks';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import AkFlag, { AutoDismissFlag, FlagGroup } from '@atlaskit/flag';
import StatusErrorIcon from '@atlaskit/icon/core/status-error';
import StatusSuccessIcon from '@atlaskit/icon/core/status-success';
import { token } from '@atlaskit/tokens';

import { dismissDiagramPasteFlag } from '../../editor-commands/dismiss-diagram-paste-flag';
import type { MalleableUiPlugin } from '../../malleableUiPluginType';
import { MauiDiagramPasteFlagType } from '../../pm-plugins/maui-diagram-paste-plugin';

type Props = {
	api?: ExtractInjectionAPI<MalleableUiPlugin>;
};

export const MauiDiagramPasteFlag = ({ api }: Props): React.JSX.Element | null => {
	const activeFlag = useSharedPluginStateWithSelector(
		api,
		['malleableUi'],
		(states) => states.malleableUiState?.diagramPaste?.activeFlag ?? null,
	);

	const onDismissed = useCallback(() => {
		api?.core.actions.execute(dismissDiagramPasteFlag());
	}, [api]);

	if (!activeFlag) {
		return null;
	}

	const FlagComponent =
		activeFlag.type === MauiDiagramPasteFlagType.SUCCESS ? AutoDismissFlag : AkFlag;

	return (
		<FlagGroup>
			<FlagComponent
				onDismissed={onDismissed}
				title={activeFlag.title}
				description={activeFlag.description}
				id={activeFlag.id}
				testId={activeFlag.id}
				icon={
					activeFlag.type === MauiDiagramPasteFlagType.ERROR ? (
						<StatusErrorIcon label="" color={token('color.icon.danger')} />
					) : (
						<StatusSuccessIcon label="" color={token('color.icon.success')} />
					)
				}
			/>
		</FlagGroup>
	);
};
